import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNavigationGuard } from "@/context/NavigationGuard";
import { useI18n } from "@/i18n/i18n";
import {
  GraduationCap, LayoutDashboard, MapPin, BookOpen, Users, Layers, UserCheck,
  Trophy, CalendarCheck, CalendarDays, Wallet, NotebookPen, ClipboardList, LogOut, Languages, UserCircle2, Bell, BarChart3, UserCog, Inbox, MessageSquareWarning, FileCog, LifeBuoy, CalendarX2, Users2, Mail, ScrollText, Megaphone, Zap, MessageCircle, PanelLeftClose, PanelLeftOpen, Handshake, PlaySquare, Radio, Receipt, ChevronDown, Camera, Mic, Store, MailCheck, Sparkles, CreditCard, Gamepad2 } from "lucide-react";
import AnnouncementModal from "@/components/AnnouncementModal";
import { useEffect, useState } from "react";
import { api, getDepartmentFilter } from "@/lib/api";
import { BrandAvatar } from "@/components/BrandAvatar";
import { DepartmentSwitcher } from "@/components/DepartmentSwitcher";
import NotificationsBell from "@/components/NotificationsBell";
import MeetingRoomButtons from "@/components/MeetingRoomButtons";
import AccountSwitcher from "@/components/AccountSwitcher";

import ImpersonationBanner from "@/components/ImpersonationBanner";
import BadgeCelebration from "@/components/BadgeCelebration";
import { LivePassBanner } from "@/components/LivePassBanner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";

// Roles with many sections use a grouped (collapsible) sidebar.
const navGroupsByRole = {
  superadmin: [
    { flat: true, items: [{ to: "/dashboard", icon: LayoutDashboard, key: "overview", end: true }] },
    {
      key: "nav_academic", icon: Layers, items: [
        { to: "/dashboard/groups", icon: Layers, key: "filter_groups" },
        { to: "/dashboard/private", icon: UserCheck, key: "filter_private" },
        { to: "/dashboard/calendar", icon: CalendarDays, key: "calendar" },
        { to: "/dashboard/attendance", icon: CalendarCheck, key: "attendance" },
        { to: "/dashboard/attendance-report", icon: CalendarCheck, key: "attendance_report" },
        { to: "/dashboard/live-sessions", icon: Radio, key: "live_sessions", badge: "live_month_left" },
        { to: "/dashboard/live-pass", icon: CreditCard, key: "live_pass", badge: "live_pass" },
        { to: "/dashboard/courses", icon: PlaySquare, key: "courses_od" },
        { to: "/dashboard/conjugation", icon: Sparkles, key: "conjugation" },
        { to: "/dashboard/resources", icon: BookOpen, key: "resources" },
        { to: "/dashboard/game-zone", icon: Gamepad2, key: "game_zone" },
        { to: "/dashboard/amigos", icon: Mic, key: "amigos" },
        { to: "/dashboard/notebooks", icon: NotebookPen, key: "notebooks" },
        { to: "/dashboard/study-plans", icon: BookOpen, key: "study_plan" },
      ],
    },
    {
      key: "nav_people", icon: Users, items: [
        { to: "/dashboard/students", icon: GraduationCap, key: "students" },
        { to: "/dashboard/teachers", icon: UserCog, key: "teachers" },
        { to: "/dashboard/users", icon: Users, key: "users", badge: "users" },
      ],
    },
    {
      key: "nav_finance", icon: Wallet, items: [
        { to: "/dashboard/payments", icon: Wallet, key: "payments" },
        { to: "/dashboard/expenses", icon: Receipt, key: "expenses" },
        { to: "/dashboard/reports", icon: BarChart3, key: "reports" },
      ],
    },
    {
      key: "nav_marketing", icon: Megaphone, items: [
        { to: "/dashboard/leads", icon: Users2, key: "leads", badge: "leads_new", end: true },
        { to: "/dashboard/leads/analytics", icon: BarChart3, key: "leads_analytics", sub: true },
        { to: "/dashboard/lead-templates", icon: Mail, key: "lead_templates" },
        { to: "/dashboard/lead-automation", icon: Zap, key: "lead_automation" },
        { to: "/dashboard/distribution", icon: Megaphone, key: "distribution" },
        { to: "/dashboard/affiliates", icon: Handshake, key: "affiliates" },
        { to: "/dashboard/pedidos", icon: Store, key: "store_orders", badge: "store_orders" },
      ],
    },
    {
      key: "nav_comms", icon: MessageCircle, items: [
        { to: "/dashboard/messages", icon: MessageCircle, key: "messages", badge: "dm_unread" },
        { to: "/dashboard/announcements", icon: Megaphone, key: "announcements" },
        { to: "/dashboard/wall", icon: Megaphone, key: "wall_board" },
        { to: "/dashboard/gallery", icon: Camera, key: "gallery" },
        { to: "/dashboard/alerts", icon: Bell, key: "alerts", badge: "alerts" },
        { to: "/dashboard/churn", icon: MessageSquareWarning, key: "churn_feedbacks", badge: "churn_feedbacks" },
      ],
    },
    {
      key: "nav_settings", icon: FileCog, items: [
        { to: "/dashboard/booking", icon: CalendarCheck, key: "booking" },
        { to: "/dashboard/absence-requests", icon: CalendarX2, key: "absence_requests", badge: "absence_requests" },
        { to: "/dashboard/sedes", icon: MapPin, key: "sedes" },
        { to: "/dashboard/programs", icon: BookOpen, key: "programs" },
        { to: "/dashboard/mail-center", icon: MailCheck, key: "mail_center" },
      ],
    },
  ],
  academic_director: [
    { flat: true, items: [{ to: "/dashboard", icon: LayoutDashboard, key: "overview", end: true }] },
    {
      key: "nav_academic", icon: Layers, items: [
        { to: "/dashboard/groups", icon: Layers, key: "filter_groups" },
        { to: "/dashboard/private", icon: UserCheck, key: "filter_private" },
        { to: "/dashboard/calendar", icon: CalendarDays, key: "calendar" },
        { to: "/dashboard/attendance", icon: CalendarCheck, key: "attendance" },
        { to: "/dashboard/attendance-report", icon: CalendarCheck, key: "attendance_report" },
        { to: "/dashboard/journal", icon: NotebookPen, key: "journal" },
        { to: "/dashboard/homework", icon: ClipboardList, key: "homework" },
        { to: "/dashboard/live-sessions", icon: Radio, key: "live_sessions", badge: "live_month_left" },
        { to: "/dashboard/conjugation", icon: Sparkles, key: "conjugation" },
        { to: "/dashboard/resources", icon: BookOpen, key: "resources" },
        { to: "/dashboard/game-zone", icon: Gamepad2, key: "game_zone" },
        { to: "/dashboard/amigos", icon: Mic, key: "amigos" },
        { to: "/dashboard/notebooks", icon: NotebookPen, key: "notebooks" },
        { to: "/dashboard/study-plans", icon: BookOpen, key: "study_plan" },
      ],
    },
    {
      key: "nav_people", icon: Users, items: [
        { to: "/dashboard/students", icon: GraduationCap, key: "students" },
        { to: "/dashboard/teachers", icon: UserCog, key: "teachers" },
      ],
    },
    {
      key: "nav_marketing", icon: Megaphone, items: [
        { to: "/dashboard/affiliates", icon: Handshake, key: "affiliates" },
      ],
    },
    {
      key: "nav_comms", icon: MessageCircle, items: [
        { to: "/dashboard/announcements", icon: Megaphone, key: "announcements" },
        { to: "/dashboard/alerts", icon: Bell, key: "alerts", badge: "alerts" },
        { to: "/dashboard/churn", icon: MessageSquareWarning, key: "churn_feedbacks", badge: "churn_feedbacks" },
      ],
    },
    {
      key: "nav_settings", icon: FileCog, items: [
        { to: "/dashboard/booking", icon: CalendarCheck, key: "booking" },
        { to: "/dashboard/absence-requests", icon: CalendarX2, key: "absence_requests", badge: "absence_requests" },
        { to: "/dashboard/sedes", icon: MapPin, key: "sedes" },
        { to: "/dashboard/programs", icon: BookOpen, key: "programs" },
      ],
    },
  ],
};

// El menú de la administración cambia según el departamento elegido arriba:
//  · Español  → todo (el de siempre)
//  · Inglés   → solo lo que usa We Speak English
//  · Todos    → solo lo consolidado de ambas escuelas (números y plata)
const NAV_KEYS_ENGLISH = [
  "overview", "filter_groups", "filter_private", "attendance", "notebooks",
  "absence_requests", "students", "teachers", "users", "payments", "reports",
  "messages", "announcements", "alerts", "sedes", "programs", "mail_center",
];
// Vista consolidada: el resumen con los totales de ambas escuelas y las finanzas de cada
// una por separado (las pantallas se abren fijadas con ?dept=...).
const NAV_CONSOLIDATED = [
  { flat: true, items: [{ to: "/dashboard", icon: LayoutDashboard, key: "overview", end: true }] },
  {
    key: "nav_finance_es", label: "Finanzas WSS", icon: Wallet, items: [
      { to: "/dashboard/payments?dept=dep_es", icon: Wallet, key: "payments_es", label: "Pagos" },
      { to: "/dashboard/expenses?dept=dep_es", icon: Receipt, key: "expenses_es", label: "Gastos" },
      { to: "/dashboard/reports?dept=dep_es", icon: BarChart3, key: "reports_es", label: "Reportes" },
    ],
  },
  {
    key: "nav_finance_en", label: "Finanzas WSE", icon: Wallet, items: [
      { to: "/dashboard/payments?dept=dep_en", icon: Wallet, key: "payments_en", label: "Pagos" },
      { to: "/dashboard/expenses?dept=dep_en", icon: Receipt, key: "expenses_en", label: "Gastos" },
      { to: "/dashboard/reports?dept=dep_en", icon: BarChart3, key: "reports_en", label: "Reportes" },
    ],
  },
];

const navForDepartment = (groups, depFilter, role) => {
  if (!groups) return groups;
  // La vista consolidada con las dos finanzas es solo para la administración general.
  if (depFilter === "") return role === "superadmin" ? NAV_CONSOLIDATED : groups;
  if (depFilter !== "dep_en") return groups;
  return groups
    .map((g) => ({ ...g, items: g.items.filter((i) => NAV_KEYS_ENGLISH.includes(i.key)) }))
    .filter((g) => g.items.length > 0);
};

const navByRole = {  admissions_lead_coordinator: [
    { to: "/dashboard/leads", icon: Users2, key: "leads", badge: "leads_new", end: true },
    { to: "/dashboard/leads/analytics", icon: BarChart3, key: "leads_analytics", sub: true },
    { to: "/dashboard/lead-templates", icon: Mail, key: "lead_templates" },
    { to: "/dashboard/lead-automation", icon: Zap, key: "lead_automation" },
    { to: "/dashboard/distribution", icon: Megaphone, key: "distribution" },
    { to: "/dashboard/profile", icon: UserCircle2, key: "profile" },
  ],
  // Primero su día de clases, después el material y al final lo social.
  teacher: [
    { to: "/dashboard", icon: LayoutDashboard, key: "my_groups", end: true },
    { to: "/dashboard/students", icon: GraduationCap, key: "my_students" },
    { to: "/dashboard/calendar", icon: CalendarDays, key: "calendar" },
    { to: "/dashboard/attendance", icon: CalendarCheck, key: "attendance" },
    { to: "/dashboard/homework", icon: ClipboardList, key: "homework", badge: "homework_unread", sep: true },
    { to: "/dashboard/journal", icon: NotebookPen, key: "journal" },
    { to: "/dashboard/class-plan", icon: BookOpen, key: "class_plan", flag: "study_plan_access" },
    { to: "/dashboard/notebooks", icon: NotebookPen, key: "notebooks" },
    { to: "/dashboard/live-sessions", icon: Radio, key: "live_sessions", badge: "live_month_left", sep: true },
    { to: "/dashboard/conjugation", icon: Sparkles, key: "conjugation" },
    { to: "/dashboard/resources", icon: BookOpen, key: "resources" },
    { to: "/dashboard/game-zone", icon: Gamepad2, key: "game_zone" },
    { to: "/dashboard/amigos", icon: Mic, key: "amigos" },
    { to: "/dashboard/messages", icon: MessageCircle, key: "messages", badge: "dm_unread", sep: true },
    { to: "/dashboard/wall-board", icon: Megaphone, key: "wall_board" },
    { to: "/dashboard/gallery", icon: Camera, key: "gallery" },
  ],
  // Orden: primero lo que usa para estudiar, al final los trámites. `sep`
  // dibuja una línea fina arriba del ítem, sin título de grupo.
  student: [
    { to: "/dashboard", icon: LayoutDashboard, key: "overview", end: true },
    { to: "/dashboard/messages", icon: MessageCircle, key: "messages", badge: "dm_unread" },
    { to: "/dashboard/live-classes", icon: Radio, key: "live_classes", badge: "live_month_left", sep: true },
    { to: "/dashboard/amigos", icon: Mic, key: "amigos" },
    { to: "/dashboard/conjugation", icon: Sparkles, key: "conjugation" },
    { to: "/dashboard/resources", icon: BookOpen, key: "resources" },
    { to: "/dashboard/game-zone", icon: Gamepad2, key: "game_zone" },
    { to: "/dashboard/my-courses", icon: PlaySquare, key: "my_courses_od" },
    { to: "/dashboard/my-homework", icon: ClipboardList, key: "my_homework", badge: "homework_pending", sep: true },
    { to: "/dashboard/my-plan", icon: BookOpen, key: "my_level_plan", flag: "study_plan_access" },
    { to: "/dashboard/my-attendance", icon: CalendarCheck, key: "my_attendance" },
    { to: "/dashboard/my-journal", icon: NotebookPen, key: "my_journal" },
    { to: "/dashboard/achievements", icon: Trophy, key: "my_achievements" },
    { to: "/dashboard/wall-board", icon: Megaphone, key: "wall_board", sep: true },
    { to: "/dashboard/gallery", icon: Camera, key: "gallery" },
    { to: "/dashboard/my-absences", icon: CalendarX2, key: "my_absences", sep: true },
    { to: "/dashboard/my-payments", icon: Wallet, key: "my_payments", badge: "my_payments" },
  ],
  affiliate: [
    { to: "/dashboard", icon: Handshake, key: "my_portal", end: true },
    { to: "/dashboard/messages", icon: MessageCircle, key: "messages", badge: "dm_unread" },
  ],
};

export default function DashboardLayout() {
  const { user, logout, policies } = useAuth();
  const dept = user?.department || null;
  const hidden = dept?.hidden_modules || [];
  // `flag` esconde un módulo que todavía está abierto solo a unas personas
  // (hoy: el Plan de estudio, mientras se arma el temario).
  const visible = (item) => !hidden.includes(item.key) && (!item.flag || !!user?.[item.flag]);
  const policiesUrl = policies?.url || "https://wespeakspanish-cr.com/about-us/policies-conditions/";
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { requestNavigate } = useNavigationGuard();
  const role = user?.role ?? "student";
  const [amigosOn, setAmigosOn] = useState(null);   // null while loading, then bool
  useEffect(() => {
    api.get("/amigos/settings").then((r) => setAmigosOn(!!r.data.allowed)).catch(() => setAmigosOn(false));
  }, []);
  const hideAmigos = (items) => (amigosOn === false ? items.filter((i) => i.key !== "amigos") : items);
  const navGroups = navForDepartment(
    (navGroupsByRole[role] || null)?.map((g) => ({ ...g, items: hideAmigos(g.items) })),
    getDepartmentFilter(),
    role,
  );
  const nav = navGroups
    ? navGroups.flatMap((g) => g.items)
    : hideAmigos(navByRole[role] ?? navByRole.student);
  // Pase de WSS Live: quien entró por la página pública ve un menú recortado, y
  // si no pagó solo le queda la pantalla de pagos.
  const [livePass, setLivePass] = useState(null);
  useEffect(() => {
    if (role !== "student") return;
    api.get("/live-pass/me").then(({ data }) => setLivePass(data.has_pass ? data : null)).catch(() => {});
  }, [role]);
  const LIVE_PASS_KEYS = ["overview", "messages", "live_classes", "conjugation",
                          "resources", "game_zone", "wall_board", "my_achievements",
                          "my_payments"];
  const navFinal = !livePass
    ? nav
    : ["blocked", "cancelled"].includes(livePass.status)
      ? nav.filter((i) => i.key === "my_payments")
      // Con pase, Mensajes va de último: lo que importa son las clases y la práctica.
      : [...nav.filter((i) => LIVE_PASS_KEYS.includes(i.key) && i.key !== "messages"),
         ...nav.filter((i) => i.key === "messages").map((i) => ({ ...i, sep: true }))];
  const [openSections, setOpenSections] = useState({});
  const [stats, setStats] = useState({});
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Sidebar collapse state persisted per browser
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "1"; }
    catch { return false; }
  });
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem("sidebar_collapsed", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  const guardedNavigate = (to) => {
    if (to === location.pathname) return;
    requestNavigate(() => navigate(to));
  };

  // Sections start closed; the one holding the current page opens automatically.
  useEffect(() => {
    if (!navGroups) return;
    const active = navGroups.find(
      (g) => !g.flat && g.items.some((i) => location.pathname === i.to || location.pathname.startsWith(`${i.to}/`))
    );
    if (active) setOpenSections((s) => (s[active.key] ? s : { ...s, [active.key]: true }));
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Más de 99 no aporta: el globito se vuelve una barra y nadie cuenta tanto.
  const corto = (n) => (n > 99 ? "99+" : n);

  // Al contraer una sección su globito suma los de adentro, para que no parezca
  // que el aviso desapareció (pedido 27/10/2026: Académico mostraba 13 en
  // "Sesiones WSS Live" y al contraerlo no mostraba nada).
  const sectionBadge = (group) =>
    group.items.reduce((n, i) => n + (i.badge ? (stats[i.badge] || 0) : 0), 0);

  const depDeLaUrl = new URLSearchParams(location.search).get("dept");
  const itemActivo = (item, isActive) => {
    const dep = item.to.includes("dept=") ? item.to.split("dept=")[1] : null;
    if (!dep) return isActive;
    return isActive && depDeLaUrl === dep;
  };

  const renderNavItem = (item) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      data-testid={`nav-${item.key}`}
      onClick={(e) => { e.preventDefault(); guardedNavigate(item.to); }}
      title={collapsed ? (item.label || t(item.key)) : undefined}
      className={({ isActive: a }) => {
        const isActive = itemActivo(item, a);
        return `sidebar-link ${isActive ? "sidebar-link-active" : ""} ${item.sub && !collapsed ? "sidebar-link-sub" : ""} ${collapsed ? "justify-center px-2" : ""} ${item.sep ? "mt-2 pt-2.5 border-t border-slate-100" : ""}`;
      }}
    >
      <item.icon className={item.sub && !collapsed ? "h-3.5 w-3.5" : "h-4 w-4"} />
      {!collapsed && <span className="flex-1">{item.label || t(item.key)}</span>}
      {item.badge && stats[item.badge] > 0 && (
        <span
          className={`${collapsed ? "absolute -top-0.5 -right-0.5" : "ml-auto"} bg-brand text-white text-xs font-bold rounded-full ${collapsed ? "min-w-[18px] h-[18px] px-1 flex items-center justify-center" : "px-2.5 py-1 min-w-[24px]"} text-center leading-tight shadow-sm`}
          title={
            item.badge === "live_month_left"
              ? t("live_month_left_hint")
              : item.badge === "homework_unread" && stats.homework_unread_names?.length
              ? `${stats[item.badge]} respuestas nuevas: ${stats.homework_unread_names.slice(0, 3).join(", ")}${stats.homework_unread_names.length > 3 ? ` y ${stats.homework_unread_names.length - 3} más` : ""}`
              : undefined
          }
        >
          {corto(stats[item.badge])}
        </span>
      )}
    </NavLink>
  );

  const renderMobileItem = (item) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      onClick={(e) => { e.preventDefault(); setMobileNavOpen(false); guardedNavigate(item.to); }}
      className={({ isActive: a }) =>
        `flex items-center gap-3 text-sm px-3 min-h-[44px] rounded-lg mb-0.5 ${
          itemActivo(item, a) ? "bg-brand-blue text-white font-semibold" : "text-slate-700 hover:bg-slate-100"
        } ${item.sep ? "mt-2 border-t border-slate-100" : ""}`
      }
      data-testid={`mobile-nav-${item.key}`}
    >
      <item.icon className="h-4 w-4 shrink-0" />
      <span className="truncate flex-1">{item.label || t(item.key)}</span>
      {item.badge && stats[item.badge] > 0 && (
        <span className="bg-brand text-white text-xs font-bold rounded-full px-2.5 py-1 min-w-[24px] text-center leading-tight shadow-sm">
          {corto(stats[item.badge])}
        </span>
      )}
    </NavLink>
  );

  useEffect(() => {
    if (role === "superadmin" || role === "academic_director") {
      api.get("/stats/overview").then((r) => setStats((s) => ({ ...s, ...r.data }))).catch(() => {});
    }
    // Load leads stats for roles that can see the leads nav item
    if (role === "superadmin" || role === "admissions_lead_coordinator") {
      const load = () => api.get("/leads/stats")
        .then((r) => setStats((s) => ({ ...s, leads_new: r.data?.by_status?.new || 0 })))
        .catch(() => {});
      load();
      const t = setInterval(load, 60000); // refresh every minute
      return () => clearInterval(t);
    }
  }, [role]);

  // Lo que espera atención de la escuela (ausencias, pedidos, pases, alertas).
  useEffect(() => {
    if (role !== "superadmin" && role !== "academic_director") return;
    const load = () => api.get("/stats/nav-badges")
      .then((r) => setStats((s) => ({ ...s, ...r.data })))
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [role, location.pathname]);

  // Globito de pagos del estudiante: facturas suyas sin pagar.
  useEffect(() => {
    if (role !== "student") return;
    const load = () => api.get("/payments")
      .then((r) => setStats((s) => ({
        ...s,
        my_payments: (r.data || []).filter((p) => p.status === "pending").length,
      })))
      .catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, [role, location.pathname]);

  // Homework badges: student (pending) or teacher (unread submissions)
  useEffect(() => {
    if (role !== "student" && role !== "teacher") return;
    const load = () => api.get("/homework/stats")
      .then((r) => setStats((s) => ({
        ...s,
        homework_pending: r.data?.pending_count || 0,
        homework_unread: r.data?.unread_count || 0,
        homework_unread_names: r.data?.unread_student_names || [],
      })))
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [role, location.pathname]);

  // DM unread badge (teacher, student, affiliate & superadmin)
  useEffect(() => {
    if (!["student", "teacher", "affiliate", "superadmin"].includes(role)) return;
    const load = () => api.get("/dm/stats")
      .then((r) => setStats((s) => ({ ...s, dm_unread: r.data?.unread_count || 0 })))
      .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [role, location.pathname]);

  // Sesiones WSS Live que quedan en el mes (baja cuando una sesión termina)
  useEffect(() => {
    if (!["student", "teacher", "superadmin", "academic_director"].includes(role)) return;
    const load = () => api.get("/live-sessions/month-count")
      .then((r) => setStats((s) => ({ ...s, live_month_left: r.data?.upcoming || 0 })))
      .catch(() => {});
    load();
    const t = setInterval(load, 300000);
    return () => clearInterval(t);
  }, [role, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}      <aside className={`hidden md:flex ${collapsed ? "w-16" : "w-64"} flex-col bg-white border-r border-slate-200 sticky top-0 h-screen transition-[width] duration-200`} data-testid="dashboard-sidebar" data-collapsed={collapsed ? "1" : "0"}>
        <div className={`${collapsed ? "px-3" : "px-5"} py-5 border-b border-slate-200 flex items-center justify-between gap-2`}>
          <Link to="/dashboard" className="flex items-center gap-2 min-w-0">
            {dept?.logo_url ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${dept.logo_url}`} alt={dept.name}
                   className="h-9 w-9 rounded-md object-contain bg-white shrink-0"
                   data-testid="sidebar-dept-logo" />
            ) : (
              <div className="h-9 w-9 bg-brand-blue text-white rounded-md flex items-center justify-center shrink-0">
                <GraduationCap className="h-5 w-5" />
              </div>
            )}
            {!collapsed && (
              <div className="min-w-0">
                <div className="font-display text-base font-bold leading-tight tracking-tight truncate"
                     data-testid="sidebar-app-name">{dept?.name || t("app_name")}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 truncate">{t(role)}</div>
              </div>
            )}
          </Link>
          <button
            type="button"
            onClick={toggleCollapsed}
            className="shrink-0 h-7 w-7 rounded-md hover:bg-slate-100 text-slate-500 flex items-center justify-center"
            title={collapsed ? "Expandir menú" : "Contraer menú"}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>

        <nav className={`flex-1 ${collapsed ? "px-1.5" : "px-3"} py-4 space-y-1 overflow-y-auto`}>
          {!collapsed && (
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 px-3 mb-2">
              {t("dashboard")}
            </div>
          )}
          {navGroups && !collapsed
            ? navGroups.map((group, gi) =>
                group.flat ? (
                  <div key={`flat-${gi}`}>{group.items.filter(visible).map((item) => renderNavItem(item))}</div>
                ) : (
                  <div key={group.key} className="pt-1">
                    <button
                      type="button"
                      onClick={() => setOpenSections((s) => ({ ...s, [group.key]: !s[group.key] }))}
                      data-testid={`nav-section-${group.key}`}
                      aria-expanded={!!openSections[group.key]}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                    >
                      <group.icon className="h-4 w-4 shrink-0" />
                      <span className="flex-1 text-left">{group.label || t(group.key)}</span>
                      {!openSections[group.key] && sectionBadge(group) > 0 && (
                        <span className="bg-brand text-white text-xs font-bold rounded-full px-2 py-0.5 min-w-[22px] text-center leading-tight shadow-sm">
                          {corto(sectionBadge(group))}
                        </span>
                      )}
                      <ChevronDown
                        className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${openSections[group.key] ? "rotate-180" : ""}`}
                      />
                    </button>
                    {openSections[group.key] && (
                      <div className="mt-0.5 space-y-0.5 pl-2 border-l border-slate-100 ml-4">
                        {group.items.filter(visible).map((item) => renderNavItem(item))}
                      </div>
                    )}
                  </div>
                )
              )
            : navFinal.filter(visible).map((item) => renderNavItem(item))}
        </nav>

        <div className={`${collapsed ? "px-1.5" : "px-3"} py-4 border-t border-slate-200`}>
          <button
            data-testid="sidebar-lang-toggle"
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            className={`sidebar-link w-full ${collapsed ? "justify-center px-2" : ""}`}
            title={collapsed ? `${t("language")} · ${lang.toUpperCase()}` : undefined}
          >
            <Languages className="h-4 w-4" />
            {!collapsed && <span>{t("language")} · {lang.toUpperCase()}</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <ImpersonationBanner />
        {user?.role === "student" && <BadgeCelebration />}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between px-6 h-16">
            <div className="md:hidden flex items-center gap-2">
              <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-11 w-11" data-testid="mobile-menu-button" aria-label={t("menu")}>
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[86vw] max-w-[320px] p-0 overflow-y-auto" data-testid="mobile-nav-drawer" aria-describedby={undefined}>
                  <SheetTitle className="sr-only">{t("menu")}</SheetTitle>
                  <div className="px-4 py-4 border-b border-slate-200 flex items-center gap-2">
                    <div className="h-9 w-9 bg-brand-blue text-white rounded-md flex items-center justify-center">
                      <GraduationCap className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-bold text-sm truncate">{t("app_name")}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{t(role)}</div>
                    </div>
                  </div>
                  <nav className="p-2">
                    {navGroups
                      ? navGroups.map((group, gi) =>
                          group.flat ? (
                            <div key={`mflat-${gi}`}>{group.items.filter(visible).map((item) => renderMobileItem(item))}</div>
                          ) : (
                            <div key={group.key} className="pt-1">
                              <button
                                type="button"
                                onClick={() => setOpenSections((s) => ({ ...s, [group.key]: !s[group.key] }))}
                                data-testid={`mobile-nav-section-${group.key}`}
                                aria-expanded={!!openSections[group.key]}
                                className="w-full flex items-center gap-3 px-3 min-h-[44px] rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100"
                              >
                                <group.icon className="h-4 w-4 shrink-0" />
                                <span className="flex-1 text-left">{group.label || t(group.key)}</span>
                                {!openSections[group.key] && sectionBadge(group) > 0 && (
                                  <span className="bg-brand text-white text-xs font-bold rounded-full px-2 py-0.5 min-w-[22px] text-center leading-tight shadow-sm">
                                    {corto(sectionBadge(group))}
                                  </span>
                                )}
                                <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-200 ${openSections[group.key] ? "rotate-180" : ""}`} />
                              </button>
                              {openSections[group.key] && (
                                <div className="pl-3 ml-3 border-l border-slate-100">
                                  {group.items.filter(visible).map((item) => renderMobileItem(item))}
                                </div>
                              )}
                            </div>
                          )
                        )
                      : navFinal.filter(visible).map((item) => renderMobileItem(item))}
                  </nav>
                </SheetContent>
              </Sheet>
              <div className="h-8 w-8 bg-brand-blue text-white rounded-md flex items-center justify-center">
                <GraduationCap className="h-4 w-4" />
              </div>
              <span className="font-display font-bold text-sm">{t("app_name")}</span>
            </div>
            <div className="text-sm text-slate-500 hidden md:block">
              {t("welcome_back")}, <span className="text-slate-900 font-medium">{user?.name}</span>
            </div>
            {user?.role === "superadmin" && <DepartmentSwitcher />}
            <NotificationsBell />
            <MeetingRoomButtons />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-button" variant="ghost" className="h-10 px-2 gap-2">
                  <BrandAvatar src={user?.picture} alt={user?.name} className="h-8 w-8" />
                  <div className="hidden sm:flex flex-col items-start leading-tight">
                    <span className="text-xs font-medium text-slate-900">{user?.name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">{t(role)}</span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-xs text-slate-500">{t("email")}</div>
                  <div className="text-sm text-slate-900 truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <AccountSwitcher />
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="dropdown-my-profile"
                  onClick={() => guardedNavigate("/dashboard/profile")}
                >
                  <UserCircle2 className="h-4 w-4 mr-2" />
                  {t("my_profile")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="dropdown-help"
                  onClick={() => guardedNavigate("/dashboard/help")}
                >
                  <LifeBuoy className="h-4 w-4 mr-2" />
                  {t("help")}
                </DropdownMenuItem>
                {role !== "affiliate" && (
                  <DropdownMenuItem
                    data-testid="dropdown-policies"
                    onClick={() => window.open(policiesUrl, "_blank", "noopener,noreferrer")}
                  >
                    <ScrollText className="h-4 w-4 mr-2" />
                    {t("policies_menu_item")}
                  </DropdownMenuItem>
                )}
                {user?.role === "superadmin" && (
                  <DropdownMenuItem
                    data-testid="dropdown-invoice-settings"
                    onClick={() => guardedNavigate("/dashboard/invoice-settings")}
                  >
                    <FileCog className="h-4 w-4 mr-2" />
                    {t("invoice_settings_short")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setLang(lang === "es" ? "en" : "es")}
                  data-testid="dropdown-lang-toggle"
                >
                  <Languages className="h-4 w-4 mr-2" />
                  {t("language")}: {lang.toUpperCase()}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-700"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

        </header>

        {livePass && <LivePassBanner pass={livePass} />}
        <main className={`flex-1 p-4 sm:p-6 lg:p-8 w-full mx-auto ${collapsed ? "" : "max-w-[1400px]"}`}>
          <AnnouncementModal />
          <div key={`${location.pathname}|${depDeLaUrl || ""}`}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
